import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { Button, Table } from 'antd';
import { TablePaginationConfig } from 'antd/es/table';
import { CardLayout, Catcher, LinkComponent, SkyPaginationTotal } from '../../';
import { ManageModal } from './ManageModal';
import { ICardSetOwnerCompany, IShowCardColumn } from '../../../interfaces';
import { useCardSetInfo, useCardSetManageChecks, useCardSetOwnerCompanies } from '../../../hooks';
import {
    CARD_SET_MANAGE_PAGE_SIZE,
    cleanCardSetManageStore,
    getCardSetManageChecks,
    getCardSetOwnerCompanies,
} from '../../../middleware';
import { ITrNotificationMessage, tB, tF, tNM } from '../../../translate';

interface ICardAssignedCompanies extends IShowCardColumn {
    isManageable?: boolean;
}

export const CardAssignedCompanies: React.FC<ICardAssignedCompanies> = observer(
    ({ columnOption, isManageable }: ICardAssignedCompanies) => {
        const [isManageOpen, setIsManageOpen] = useState(false);
        const { id, companyId } = useCardSetInfo();
        const list = useCardSetOwnerCompanies();
        const { childrenCount, cardsCount } = useCardSetManageChecks();
        const manageDisabledReason: ITrNotificationMessage | undefined = !childrenCount
            ? 'Company has no child companies'
            : !cardsCount
            ? 'Card set has no cards'
            : undefined;

        useEffect(() => {
            if (isManageable) {
                id && companyId && getCardSetManageChecks(id, companyId);
            } else {
                id && getCardSetOwnerCompanies(id);
            }
        }, [id, companyId, isManageable]);

        useEffect(() => {
            return (): void => {
                cleanCardSetManageStore();
            };
        }, []);

        const tableColumns = [
            {
                title: tF('Company'),
                dataIndex: 'companyLink',
                ellipsis: true,
            },
        ];

        const companiesCount = list.filter((r: ICardSetOwnerCompany) => r.companyId).length;

        const tableData = list.map((r: ICardSetOwnerCompany) => ({
            key: r.companyId || r.company,
            companyLink: (
                <>
                    {!r.companyId ? (
                        r.company
                    ) : (
                        <LinkComponent currentListComponent={'CompaniesList'} id={r.companyId} name={r.company} />
                    )}{' '}
                    ({r.cardsCount})
                </>
            ),
        }));

        const pagination: false | TablePaginationConfig =
            CARD_SET_MANAGE_PAGE_SIZE >= list.length
                ? false
                : {
                      total: list.length,
                      pageSize: CARD_SET_MANAGE_PAGE_SIZE,
                      className: 'customPagination',
                      showSizeChanger: false,
                      size: 'default',
                      showTotal: SkyPaginationTotal,
                  };

        return (
            <Catcher>
                <CardLayout
                    columnOption={columnOption}
                    count={companiesCount}
                    idSpinners={['GET_CARD_SET_OWNER_COMPANIES']}
                    isTable
                    title={'Companies with assigned cards'}
                    OtherButton={
                        isManageable ? (
                            <Button
                                type={'primary'}
                                onClick={(): void => setIsManageOpen(true)}
                                disabled={!!manageDisabledReason}
                                title={manageDisabledReason ? tNM(manageDisabledReason) : undefined}
                            >
                                {tB('Manage')}
                            </Button>
                        ) : null
                    }
                >
                    <Table
                        dataSource={tableData}
                        columns={tableColumns}
                        pagination={pagination}
                        className={'table-theme sub-table'}
                        size={'middle'}
                    />
                </CardLayout>
                {isManageable ? (
                    <ManageModal isOpen={isManageOpen} onClose={(): void => setIsManageOpen(false)} />
                ) : null}
            </Catcher>
        );
    },
);
